import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, inr, num, fmtDate, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, FileSpreadsheet, ArrowRight } from "lucide-react";

export default function Analyses({ reports = false }) {
  const [items, setItems] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/analyses").then(({ data }) => setItems(data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const list = reports ? items.filter((i) => i.status === "ready" && i.mode === "bank_statement") : items;

  return (
    <div className="space-y-6" data-testid={reports ? "reports-page" : "analyses-page"}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {reports ? "Exports" : "History"}
        </p>
        <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tighter">
          {reports ? "Reports" : "Analyses"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {reports ? "Generate Excel workbooks and PDF financial analysis reports from completed analyses."
                   : "Every extraction batch, with its combined transactions and analysis."}
        </p>
      </div>

      {list.length === 0 ? (
        <div className="rounded-lg border border-border bg-white p-12 text-center" data-testid="analyses-empty">
          <p className="text-sm text-muted-foreground">Nothing here yet.</p>
          <Button className="mt-4 rounded-full" onClick={() => nav("/analyzer")} data-testid="analyses-empty-cta">
            Analyze a statement
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {list.map((a) => (
            <div key={a.job_id} className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-white p-4"
                 data-testid={`analysis-card-${a.job_id}`}>
              <div className="min-w-[220px] flex-1">
                <p className="truncate font-medium">{(a.file_names || []).join(", ") || a.job_id}</p>
                <p className="text-xs text-muted-foreground">
                  {a.mode === "bank_statement" ? "Bank Statement" : "Image → Excel"} · {fmtDate(a.created_at)}
                  {a.bank_name ? ` · ${a.bank_name}` : ""}
                </p>
              </div>
              {a.is_demo && <Badge className="bg-warn text-warn-foreground">DEMO</Badge>}
              <Badge variant="outline">{a.status}</Badge>
              <span className="num text-sm">{num(a.transaction_count)} txns</span>
              <span className="num text-sm text-green-700">{inr(a.total_credits)}</span>
              <span className="num text-sm text-red-700">{inr(a.total_debits)}</span>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" title="Export Excel"
                        onClick={() => downloadFile(`/export/excel/${a.job_id}`, "GrowRupee.xlsx")
                          .then(() => toast.success("Excel exported")).catch((e) => toast.error(errMsg(e)))}
                        data-testid={`analysis-excel-${a.job_id}`}><FileSpreadsheet className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" title="Generate Report"
                        onClick={() => downloadFile(`/export/pdf/${a.job_id}`, "GrowRupee_Report.pdf")
                          .then(() => toast.success("Report generated")).catch((e) => toast.error(errMsg(e)))}
                        data-testid={`analysis-pdf-${a.job_id}`}><FileText className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" className="bg-white" onClick={() => nav(`/workspace/${a.job_id}`)}
                        data-testid={`analysis-open-${a.job_id}`}>
                  Open <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
